import { Api } from "../../Services/Api";
import { BookingAuthority } from "../../Services/BookingEntities";
import { Dispatch } from "../Dispatch";
import { NoticeType, WellknownErrorMessages } from "./UI/Notifications/NotificationEntities";
import { LogEvent } from "../../utils/LogEvent";
import { LogEventInGA4 } from "../../utils/LogEventsInGoogleAnalytics";
import appstore from "../../appStore";
import moment from "moment";

/**
 * Cancel a phone booking by calling the cancel booking API.
 * This function uses the existing booking cancellation mechanism.
 * 
 * @param bookingId - The booking ID to cancel
 * @param accessCode - The access code (hashCode) for the booking
 * @param userId - The user ID (or guest identifier) for logging purposes
 * @returns Promise<boolean> - Returns true if cancellation was successful, false otherwise
 */
export async function CancelPhoneToCnpBooking(
    bookingId: number,
    accessCode: string,
    userId: string
): Promise<boolean> {
    
    // Log the cancellation attempt
    LogEvent.PerformedBookingCancellation();
    
    try {
        // Create the booking authority object required by the API
        const bookingKey: BookingAuthority = {
            bookingId: bookingId,
            hashCode: accessCode,
        };

        var result = await Api.Booking.CancelByKey(bookingKey);

        if (result.isSuccess) {
            // Log successful cancellation in GA4
            LogEventInGA4.CancelBooking(userId, bookingId);
            // hide the notice about fixed price not locked in. It is not relevant anymore once the booking is picked up.
            if (appstore.getState().PhoneToCnp.NoticeToDisplay === NoticeType.FixedPriceNotLockedIn) {
                Dispatch.PhoneToCnp.HideNotice();
            }

            // We display last updated time from the server as the cancelled time. But just after cancellation, we don't have the server time yet. Therefore, we update the LastUpdateTime in Redux store to the current time (the time the cancellation successful event occured) so that the user sees the cancellation time until the page is refreshed. Make this time in the timezone of the booking, if available, otherwise use user's timezone.
            const bookingDetails = appstore.getState().PhoneToCnp.Booking!;
            const bookingTimeZone = bookingDetails.Timing.Time ? moment.parseZone(bookingDetails.Timing.Time).format("Z") : undefined;
            const newBookingStatus = {
                ...bookingDetails,
                LastUpdated: bookingTimeZone ? moment().utcOffset(bookingTimeZone).toString() : moment().toString()
            };

            Dispatch.PhoneToCnp.RefreshBookingDetails(newBookingStatus);

            return true;
        } else {
            // Show error message if API call failed
            Dispatch.PhoneToCnp.ShowErrorMessage(WellknownErrorMessages.CancelBookingError);
            return false;
        }
    } catch (error) {
        // Handle any unexpected errors
        Dispatch.PhoneToCnp.ShowErrorMessage(WellknownErrorMessages.CancelBookingError);
        return false;
    }
}
